import type { Discussion } from "./types";
import { discussions } from "@/mocks/discussion";
import DiscussionCard from "./DiscussionCard";

export type OrderKey = "hot" | "latest" | "pick";

const TABS: { key: OrderKey; label: string }[] = [
  { key: "hot", label: "🔥 热议飙升" },
  { key: "latest", label: "最新围观" },
  { key: "pick", label: "编辑精选" },
];

export default function HotFeed({
  query,
  category,
  order,
  setOrder,
  onOpen,
  onReset,
}: {
  query: string;
  category: string;
  order: OrderKey;
  setOrder: (o: OrderKey) => void;
  onOpen: (t: Discussion) => void;
  onReset: () => void;
}) {
  let list = discussions.filter(
    (d) =>
      (!category || d.categoryName === category) &&
      (!query ||
        d.title.includes(query) ||
        d.summary.includes(query) ||
        d.categoryName.includes(query) ||
        d.author.name.includes(query))
  );

  if (order === "latest") list = [...list].reverse();
  if (order === "pick") list = [...list].filter((d) => d.tag === "新" || d.tag === "议");

  return (
    <section id="feed" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-[1fr_320px]">
        {/* 主列表 */}
        <div className="min-w-0">
          <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-primary-500">热议广场</p>
              <h2 className="section-title mt-2 text-2xl text-foreground-950 md:text-3xl">
                {category || "全部话题"} · 实时讨论区
              </h2>
            </div>
            <div className="flex items-center rounded-full bg-background-100 p-1">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setOrder(tab.key)}
                  className={
                    order === tab.key
                      ? "whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-xs font-medium text-primary-600 shadow-soft"
                      : "whitespace-nowrap rounded-full px-4 py-2 text-xs text-foreground-500 hover:text-foreground-800"
                  }
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* 当前筛选提示 */}
          {(category || query) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <span>当前围观范围：</span>
              {category && (
                <span className="rounded-full bg-primary-100 px-2.5 py-1 font-medium text-primary-700">{category}</span>
              )}
              {query && (
                <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-2.5 py-1 font-medium text-secondary-900">
                  “{query}”
                  <i className="ri-close-circle-line cursor-pointer" onClick={() => onReset()} aria-hidden="true"></i>
                </span>
              )}
            </div>
          )}

          {list.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true"></i>
              <p className="mt-3 text-sm text-foreground-600">
                暂时没找到「{query || category}」相关话题，换个热词试试，或到下方投稿开启讨论～
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              {list.map((topic, i) => (
                <DiscussionCard key={topic.id} topic={topic} onOpen={onOpen} />
              ))}
            </div>
          )}
        </div>

        {/* 右侧热榜 */}
        <TrendingSidebar onOpen={onOpen} />
      </div>
    </section>
  );
}

function TrendingSidebar({ onOpen }: { onOpen: (t: Discussion) => void }) {
  const top = discussions.filter((d) => d.rank <= 6);
  return (
    <aside className="lg:sticky lg:top-24 lg:self-start">
      <div className="rounded-3xl border border-background-200 bg-background-100 p-5">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
            <i className="ri-bar-chart-2-fill text-primary-500" aria-hidden="true"></i>
            24h 热议热榜
          </h2>
          <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-semibold text-secondary-900">实时更新</span>
        </div>

        <ol className="mt-4 space-y-1">
          {top.map((topic, i) => (
            <li key={topic.id}>
              <button
                type="button"
                onClick={() => onOpen(topic)}
                className="group flex w-full cursor-pointer items-start gap-3 rounded-xl p-2 text-left transition-colors hover:bg-background-50"
              >
                <span
                  className={
                    i < 3
                      ? "flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-sm text-white"
                      : "flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-200 text-xs font-bold text-foreground-600"
                  }
                >
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-[13px] font-medium leading-snug text-foreground-800 group-hover:text-primary-600">
                    {topic.title}
                  </span>
                  <span className="mt-1 block text-[11px] text-foreground-400">{topic.comments} 条讨论 · 热度 {topic.heat}</span>
                </span>
                <span className="mt-0.5 flex items-center gap-0.5 text-[11px] font-semibold text-primary-500">
                  {topic.tag}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-4 rounded-3xl bg-accent-500/10 p-5 ring-1 ring-accent-200/60">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-500 text-white">
          <i className="ri-megaphone-line text-lg" aria-hidden="true"></i>
        </span>
        <h3 className="mt-3 text-[15px] font-bold text-foreground-950">你有瓜要爆？</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-foreground-600">
          掌握第一手新鲜事，投稿上热榜，让全网围观你的爆料。
        </p>
        <a
          href="#submit"
          className="mt-3 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-foreground-950 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-foreground-800"
        >
          去投稿
          <i className="ri-arrow-right-up-line" aria-hidden="true"></i>
        </a>
      </div>
    </aside>
  );
}